import { get } from "svelte/store";
import type { RoomConnection } from "../Connection/RoomConnection";
import { emoteStore } from "../Stores/EmoteStore";
import { enableUserInputsStore } from "../Stores/UserInputStore";

/**
 * Coros de la oficina: sonidos que suben de intensidad con la cantidad de
 * gente que aprieta una tecla. Hay dos:
 *
 *   F   recarga de pistola → un disparo por pulsación → ráfaga
 *   3   aplauso de una persona → ovación
 *
 * Cada pulsación viaja como un evento de scripting sin destinatarios, que el
 * back reparte a todos los presentes en la sala (incluido quien aprieta), así
 * que no hace falta tocar el protocolo. Cada cliente cuenta las pulsaciones
 * que le llegan en los últimos segundos y decide solo qué suena; como todos
 * reciben lo mismo, todos oyen lo mismo.
 *
 * Los sonidos van por HTMLAudio y no por el cargador de Phaser: así no hay
 * que tocar la escena, y se sirven como cualquier recurso estático. Todos los
 * MP3 los aportó el usuario.
 */
const RUTA = "/resources/objects/insecap/";
const VENTANA_MS = 5000;
const ENTRE_PULSACIONES_MS = 200;

type Nivel = 0 | 1 | 2 | 3;

interface Sonidos {
    evento: string;
    emoji: string;
    nombre: string;
    /** Nivel 1: una vez por pulsación. */
    pocas: string;
    /** Nivel 2: una vez por pulsación. */
    varias: string;
    /** Nivel 3: en bucle o repetido cada `cadaMs`, mientras dure. */
    muchas: string;
    modoMuchas: { bucle: true } | { cadaMs: number };
}

class Coro {
    private marcas: { t: number; quien: number | undefined }[] = [];
    private ultimoEnvio = 0;
    private rafaga: number | undefined;
    private bucle: HTMLAudioElement | undefined;
    private revision: number | undefined;

    constructor(private readonly s: Sonidos) {}

    /** Quien aprieta la tecla: emote local (que el motor ya sincroniza) y evento a la sala. */
    public pulsar(connection: RoomConnection | undefined): void {
        const ahora = Date.now();
        if (ahora - this.ultimoEnvio < ENTRE_PULSACIONES_MS) return;
        this.ultimoEnvio = ahora;
        if (get(enableUserInputsStore)) {
            emoteStore.set({ emoji: this.s.emoji, name: this.s.nombre });
        }
        connection?.emitScriptableEvent(this.s.evento, {}, undefined).catch((e) => console.warn(this.s.nombre, e));
    }

    /** Llega una pulsación de alguien (o la propia, que también vuelve del back). */
    public recibir(senderId: number | undefined): void {
        const ahora = Date.now();
        this.marcas.push({ t: ahora, quien: senderId });
        this.podar(ahora);
        const nivel = this.nivel();
        if (nivel >= 3) {
            this.empezarMuchas();
        } else if (nivel === 2) {
            this.sonar(this.s.varias, 0.8);
        } else {
            this.sonar(this.s.pocas, 0.6);
        }
        this.vigilar();
    }

    private podar(ahora: number): void {
        this.marcas = this.marcas.filter((m) => ahora - m.t < VENTANA_MS);
    }

    /** Cuenta pulsaciones y manos distintas: dos personas valen más que una insistiendo. */
    private nivel(): Nivel {
        const n = this.marcas.length;
        if (n === 0) return 0;
        const manos = new Set(this.marcas.map((m) => m.quien)).size;
        const puntaje = n + 2 * (manos - 1);
        if (puntaje >= 16) return 3;
        if (puntaje >= 6) return 2;
        return 1;
    }

    private sonar(ruta: string, volumen: number): void {
        const a = new Audio(ruta);
        a.volume = volumen;
        a.playbackRate = 0.92 + Math.random() * 0.16;
        a.play().catch(() => {});
    }

    private empezarMuchas(): void {
        if ("bucle" in this.s.modoMuchas) {
            if (this.bucle && !this.bucle.paused) return;
            const a = new Audio(this.s.muchas);
            a.loop = true;
            a.volume = 1;
            a.play().catch(() => {});
            this.bucle = a;
        } else {
            if (this.rafaga !== undefined) return;
            const cada = this.s.modoMuchas.cadaMs;
            this.rafaga = window.setInterval(() => this.sonar(this.s.muchas, 1), cada);
        }
    }

    /** La ráfaga se corta en seco; el bucle baja en un segundo. */
    private pararMuchas(): void {
        if (this.rafaga !== undefined) {
            clearInterval(this.rafaga);
            this.rafaga = undefined;
        }
        const a = this.bucle;
        this.bucle = undefined;
        if (!a) return;
        const paso = a.volume / 12;
        const bajar = window.setInterval(() => {
            a.volume = Math.max(0, a.volume - paso);
            if (a.volume <= 0.01) {
                a.pause();
                clearInterval(bajar);
            }
        }, 80);
    }

    /** Cada medio segundo: si la gente paró, lo de "muchas" se apaga. */
    private vigilar(): void {
        if (this.revision !== undefined) return;
        this.revision = window.setInterval(() => {
            this.podar(Date.now());
            if (this.nivel() < 3) this.pararMuchas();
            if (this.marcas.length === 0) {
                clearInterval(this.revision);
                this.revision = undefined;
            }
        }, 500);
    }
}

export const disparos = new Coro({
    evento: "insecap:disparo",
    emoji: "🔫",
    nombre: "disparo",
    pocas: RUTA + "recarga.mp3",
    varias: RUTA + "disparo.mp3",
    muchas: RUTA + "disparo.mp3",
    modoMuchas: { cadaMs: 140 },
});

export const aplausos = new Coro({
    evento: "insecap:aplauso",
    emoji: "👏",
    nombre: "aplauso",
    pocas: RUTA + "aplauso.mp3",
    varias: RUTA + "aplauso.mp3",
    muchas: RUTA + "ovacion.mp3",
    modoMuchas: { bucle: true },
});

const coros = [disparos, aplausos];
const porEvento = new Map(coros.map((c) => [c["s"].evento, c]));

/** Para el receptor de eventos: true si el evento era de un coro y ya se atendió. */
export function recibirCoro(nombre: string, senderId: number | undefined): boolean {
    const c = porEvento.get(nombre);
    if (!c) return false;
    c.recibir(senderId);
    return true;
}
